import { Injectable, inject, signal, computed, WritableSignal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { AppStateService } from './app-state.service';
import { CommentComponentViewService, CommentWrapperContext } from './comment-component-view.service';

/**
 * The drive explorer's feed sidebar (@mtm/media-studio `StudioCacheService`, key `sidebar-width`)
 * stores its drag width here as a JSON number. Sharing the key keeps one panel width everywhere:
 * project drive feed, org drive feed, task and comment panels.
 */
const WIDTH_STORAGE_KEY = 'mtm-studio:sidebar-width';
/** The drive explorer's frame: the gap between its edges and the window's. */
const DRIVE_FRAME_PX = 4;

export interface CommentPanelWidthState {
  width: number;
  isResizing: boolean;
  isInToleranceRange: boolean;
  isFullMode: boolean;
  isTransitioningFullSize: boolean;
  previousExpandedState: boolean;
  previousFullSizeState: boolean;
  wasFullModeBeforeMobile?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CommentPanelWidthService {
  private readonly document = inject(DOCUMENT);
  private readonly appState = inject(AppStateService);
  private readonly commentComponentViewService = inject(CommentComponentViewService);

  private readonly defaultWidth = 366;
  /** Floor for the org feed's capped drag (the explorer feed sidebar's minimum). */
  readonly minWidth = 352;
  private readonly maxViewportPercent = 0.5;
  private readonly toleranceViewportPercent = 0.4;
  private readonly resizeDebounceMs = 150;

  private readonly state: WritableSignal<CommentPanelWidthState> = signal<CommentPanelWidthState>({
    width: this.defaultWidth,
    isResizing: false,
    isInToleranceRange: false,
    isFullMode: false,
    isTransitioningFullSize: false,
    previousExpandedState: false,
    previousFullSizeState: false,
    wasFullModeBeforeMobile: false,
  });

  private resizeTimeout: ReturnType<typeof setTimeout> | null = null;
  private commentWrapperWidthOverride: number | null = null;
  private commentWrapperOpenAnimationFrameId: number | null = null;
  private previousWrapperWidth = 0;
  private readonly wrapperVisibilityState = {
    previousWidth: 0,
    isClosing: false,
    shouldHide: false,
  };

  // Public signals
  readonly width = computed(() => this.state().width);
  readonly isResizing = computed(() => this.state().isResizing);
  readonly isInToleranceRange = computed(() => this.state().isInToleranceRange);
  readonly isFullMode = computed(() => this.state().isFullMode);
  readonly isTransitioningFullSize = computed(() => this.state().isTransitioningFullSize);
  readonly shouldShowBlur = computed(() => {
    const s = this.state();
    if (s.isTransitioningFullSize || s.isFullMode) {
      return false;
    }
    return s.isResizing && s.isInToleranceRange;
  });

  // Computed values
  /**
   * The drive explorer's feed sidebar stops at 40% of the explorer, which is the window minus the
   * explorer's 4px frame on each side; the same base here keeps the task panel and the feed
   * stopping at the same width.
   */
  readonly toleranceWidth = computed(() => {
    const viewportWidth = this.document.defaultView?.innerWidth ?? 0;
    if (viewportWidth <= 0) {
      return this.defaultWidth;
    }
    return Math.floor((viewportWidth - DRIVE_FRAME_PX * 2) * this.toleranceViewportPercent);
  });

  readonly maxWidth = computed(() => {
    const viewportWidth = this.document.defaultView?.innerWidth ?? 0;
    if (viewportWidth <= 0) {
      return this.defaultWidth;
    }
    return Math.floor(viewportWidth * this.maxViewportPercent);
  });

  constructor() {
    this.syncFromStorage();
    // The explorer feed saves its own drags; pick them up when the user moves to another page.
    inject(Router)
      .events.pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => this.syncFromStorage());
  }

  /** Adopts the stored width (possibly written by the explorer feed) unless a drag or full mode owns it. */
  syncFromStorage(): void {
    const s = this.state();
    if (s.isResizing || s.isFullMode) {
      return;
    }
    const width = this.getSavedWidth();
    this.appState.setCommentPanelWidth(width);
    if (width !== s.width) {
      this.updateState({ width });
    }
  }

  private getSavedWidth(): number {
    const savedWidth = this.readStoredWidth() ?? this.appState.getCommentPanelWidth();
    return Math.max(this.defaultWidth, savedWidth || this.defaultWidth);
  }

  private readStoredWidth(): number | null {
    try {
      const stored = Number(this.document.defaultView?.localStorage.getItem(WIDTH_STORAGE_KEY));
      return stored > 0 ? stored : null;
    } catch {
      return null;
    }
  }

  /** Last width the comment / task / org feed panel was left at — survives reloads. Full mode is not a width to restore. */
  private saveWidth(width: number, isFullMode: boolean): void {
    this.appState.setCommentPanelWidth(width);
    if (isFullMode) {
      return;
    }
    try {
      this.document.defaultView?.localStorage.setItem(WIDTH_STORAGE_KEY, String(width));
    } catch {
      // Storage blocked (private mode): the in-memory width still applies for this session.
    }
  }

  private updateState(partial: Partial<CommentPanelWidthState>): void {
    const current = this.state();
    this.state.set({ ...current, ...partial });
  }

  getWidth(): number {
    return this.state().width;
  }

  getToleranceWidth(): number {
    return this.toleranceWidth();
  }

  getMaxWidth(): number {
    return this.maxWidth();
  }

  handleWidthChange(
    newWidth: number,
    options: {
      isCommentLoading: boolean;
      isCommentPluginReady: boolean;
      wasInFullMode: boolean;
    }
  ): void {
    // Don't allow resizing when comment component is loading or plugin is not ready
    if (options.isCommentLoading || !options.isCommentPluginReady) {
      return;
    }

    const viewportWidth = this.document.defaultView?.innerWidth ?? 0;
    if (viewportWidth <= 0) {
      this.updateState({ width: newWidth });
      return;
    }

    const toleranceWidth = this.toleranceWidth();
    const maxWidth = this.maxWidth();

    // Mark as resizing when width changes
    const currentState = this.state();
    if (!currentState.isResizing) {
      this.updateState({
        isResizing: true,
        isInToleranceRange: false,
      });
    }

    // If coming from full mode, cap at tolerance width
    let adjustedWidth = newWidth;
    if (options.wasInFullMode && newWidth < maxWidth) {
      adjustedWidth = Math.min(newWidth, toleranceWidth);
    }

    // Check if we're in the tolerance-to-max range
    const isInToleranceRange = adjustedWidth >= toleranceWidth && adjustedWidth < maxWidth;

    if (isInToleranceRange) {
      // Don't update the panel width, keep it at tolerance width
      // But track that we're in this range for blur display
      this.updateState({
        isInToleranceRange: true,
        width: toleranceWidth,
      });
    } else {
      // Normal resize below tolerance width or from full mode (capped at tolerance)
      this.updateState({
        width: adjustedWidth,
        isInToleranceRange: false,
      });
    }

    this.scheduleResizeEnd();
  }

  /**
   * Drag resize without full-size mode (the drive feed and task panels): clamped between the
   * minimum and tolerance widths, sharing the stored width with the comment panel.
   */
  handleCappedWidthChange(newWidth: number): void {
    this.updateState({
      width: Math.max(this.minWidth, Math.min(newWidth, this.toleranceWidth())),
      isResizing: true,
      isInToleranceRange: false,
    });
    this.scheduleResizeEnd();
  }

  /** Ends the resize after 150ms without changes and saves the width it settled on. */
  private scheduleResizeEnd(): void {
    if (this.resizeTimeout !== null) {
      globalThis.clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = globalThis.setTimeout(() => {
      const finalState = this.state();
      this.updateState({
        isResizing: false,
        isInToleranceRange: finalState.isFullMode ? finalState.isInToleranceRange : false,
      });
      this.resizeTimeout = null;
      this.saveWidth(finalState.width, finalState.isFullMode);
    }, this.resizeDebounceMs);
  }

  setFullMode(isFullMode: boolean): void {
    const currentState = this.state();
    this.updateState({
      isFullMode,
      // Clear tolerance range flag when entering full mode
      isInToleranceRange: isFullMode ? false : currentState.isInToleranceRange,
    });
  }

  handleReset(): void {
    // When reset is clicked, collapse the panel to its minimum width.
    // Use setTimeout to ensure this runs after the sash component's reset logic.
    setTimeout(() => {
      this.updateState({
        width: this.defaultWidth,
        isFullMode: false,
        isInToleranceRange: false,
        isResizing: false,
      });
      this.saveWidth(this.defaultWidth, false);
    }, 0);
  }

  handleExpandedStateChange(isExpanded: boolean): void {
    const currentState = this.state();
    // Reset width when comment panel is toggled (state changes)
    if (currentState.previousExpandedState !== isExpanded) {
      this.updateState({
        width: this.getSavedWidth(),
        previousExpandedState: isExpanded,
      });
    }
  }

  handleFullSizeStateChange(isFullSize: boolean): void {
    const currentState = this.state();
    const previousFullSize = currentState.previousFullSizeState;

    // Only apply animation when transitioning FROM fullsize TO normal width
    if (previousFullSize && isFullSize === false) {
      this.updateState({
        isTransitioningFullSize: true,
        previousFullSizeState: isFullSize,
      });
      // Remove transition class after animation completes
      setTimeout(() => {
        this.updateState({ isTransitioningFullSize: false });
      }, 400); // Match the transition duration (0.4s)
      this.appState.setCommentPanelFullSize(isFullSize);
      return;
    }

    if (isFullSize === previousFullSize) {
      this.updateState({ previousFullSizeState: isFullSize });
      this.appState.setCommentPanelFullSize(isFullSize);
      return;
    }

    // When going from normal to fullsize, disable animation immediately
    this.updateState({
      isTransitioningFullSize: false,
      previousFullSizeState: isFullSize,
    });

    this.appState.setCommentPanelFullSize(isFullSize);
  }

  getAnimatedWrapperWidth(calculatedWidth: number): number {
    return this.commentWrapperWidthOverride ?? calculatedWidth;
  }

  syncWrapperOpenAnimation(calculatedWidth: number): void {
    const isExpanding = calculatedWidth > 0 && this.previousWrapperWidth === 0;
    if (isExpanding) {
      this.queueCommentWrapperOpenAnimation();
    }

    this.previousWrapperWidth = calculatedWidth;
  }

  getCalculatedWrapperWidth(options: {
    isCommentFeatureAllowed: boolean;
    reserveCommentPanelWidthDuringNavigation: boolean;
    commentPanelWidth: number;
    context: CommentWrapperContext;
  }): number {
    if (!options.isCommentFeatureAllowed) {
      return 0;
    }
    if (options.reserveCommentPanelWidthDuringNavigation) {
      return options.commentPanelWidth;
    }
    return this.commentComponentViewService.getCommentWrapperWidth(options.context);
  }

  /** `instant`: the close runs without a transition (no `transitionend` to wait for), so hide at once. */
  syncWrapperVisibility(calculatedWidth: number, instant = false): { shouldHide: boolean; isClosing: boolean } {
    const state = this.wrapperVisibilityState;

    if (calculatedWidth > 0) {
      state.shouldHide = false;
      state.isClosing = false;
    } else if (calculatedWidth === 0 && state.previousWidth > 0) {
      state.shouldHide = instant;
      state.isClosing = !instant;
    } else if (calculatedWidth === 0 && !state.isClosing) {
      state.shouldHide = true;
    }

    state.previousWidth = calculatedWidth;
    return { shouldHide: state.shouldHide, isClosing: state.isClosing };
  }

  handleWrapperTransitionEnd(
    event: TransitionEvent,
    currentWidth: number
  ): { shouldHide: boolean; isClosing: boolean } {
    const state = this.wrapperVisibilityState;

    if (!state.isClosing) {
      return { shouldHide: state.shouldHide, isClosing: state.isClosing };
    }

    if (event.propertyName !== 'width' && event.propertyName !== 'opacity') {
      return { shouldHide: state.shouldHide, isClosing: state.isClosing };
    }

    if (currentWidth === 0) {
      state.shouldHide = true;
      state.isClosing = false;
    }

    return { shouldHide: state.shouldHide, isClosing: state.isClosing };
  }

  handleMobileSizeChange(isMobile: boolean, isCommentExpanded: boolean): void {
    const currentState: CommentPanelWidthState = this.state();
    if (isMobile) {
      // Save the full mode state before we clear it for mobile
      this.updateState({ wasFullModeBeforeMobile: currentState.isFullMode });

      // Reset full mode when switching to mobile view
      if (currentState.isFullMode) {
        this.setFullMode(false);
        // Reset comment panel width to saved size when switching to mobile
        if (isCommentExpanded) {
          this.updateState({ width: this.getSavedWidth() });
        }
      }
    }
  }

  public handleDesktopSizeChange(isCommentExpanded: boolean): void {
    const currentState: CommentPanelWidthState = this.state();
    if (currentState.wasFullModeBeforeMobile) {
      // Restore full mode when switching back to desktop view
      this.setFullMode(true);
      const viewportWidth = this.document.defaultView?.innerWidth ?? 0;
      if (viewportWidth > 0) {
        this.updateState({ width: viewportWidth });
      }
      this.updateState({ wasFullModeBeforeMobile: false });
    }
  }

  cleanup(): void {
    if (this.resizeTimeout !== null) {
      globalThis.clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
    if (this.commentWrapperOpenAnimationFrameId !== null) {
      globalThis.cancelAnimationFrame(this.commentWrapperOpenAnimationFrameId);
      this.commentWrapperOpenAnimationFrameId = null;
    }
  }

  private queueCommentWrapperOpenAnimation(): void {
    if (this.commentWrapperWidthOverride === 0) {
      return;
    }

    this.commentWrapperWidthOverride = 0;

    if (this.commentWrapperOpenAnimationFrameId !== null) {
      globalThis.cancelAnimationFrame(this.commentWrapperOpenAnimationFrameId);
    }

    this.commentWrapperOpenAnimationFrameId = globalThis.requestAnimationFrame(() => {
      this.commentWrapperWidthOverride = null;
      this.commentWrapperOpenAnimationFrameId = null;
    });
  }
}
